import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import api, { resolveImage } from "@/lib/api";
import Seo from "@/components/Seo";

const PORTRAIT_FALLBACK =
  "https://images.pexels.com/photos/7968085/pexels-photo-7968085.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function About() {
  const [author, setAuthor] = useState(null);
  const [pages, setPages] = useState(null);
  useEffect(() => {
    api.get("/author").then((r) => setAuthor(r.data)).catch(() => setAuthor({}));
    api.get("/pages").then((r) => setPages(r.data)).catch(() => {});
  }, []);

  const name = author?.name || "Barbara Ruffing";
  const tagline = author?.tagline || "Author · Poet · Faith Writer";
  const bio =
    author?.bio ||
    "Barbara Ruffing is an author with her first published children's book.";
  const photo = resolveImage(author?.photo) || PORTRAIT_FALLBACK;
  const quote = author?.quote || "Words rightly placed can carry someone through.";
  const ctaLabel = pages?.about?.cta_label || "Read the Writings";

  return (
    <div className="bg-paper" data-testid="about-page">
      <Seo
        title={`About ${name}`}
        description={bio?.slice(0, 200) || `About ${name}, author and faith writer.`}
        image={photo}
      />
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 rise-in">
            <p
              data-testid="about-tagline"
              className="text-xs uppercase tracking-[0.22em] text-terracotta mb-6"
            >
              {tagline}
            </p>
            <h1
              data-testid="about-name"
              className="font-serif-display text-3xl lg:text-4xl tracking-tight text-ink leading-[1.05]"
            >
              {name.split(" ").length > 1 ? (
                <>
                  {name.split(" ").slice(0, -1).join(" ")}{" "}
                  <span className="italic">{name.split(" ").slice(-1)[0]}</span>
                </>
              ) : (
                name
              )}
            </h1>
            <div
              data-testid="about-bio"
              className="mt-8 prose-editorial max-w-xl whitespace-pre-wrap"
            >
              {bio}
            </div>
            <Link
              to="/writings"
              data-testid="about-cta-writings"
              className="mt-10 inline-block px-6 py-3 bg-terracotta text-paper text-sm tracking-wide uppercase hover:bg-ink transition-colors"
            >
              {ctaLabel}
            </Link>

            <div className="mt-10 pt-8 border-t border-rule">
              <p className="text-xs uppercase tracking-[0.18em] text-ink-soft mb-3">
                Follow along
              </p>
              <a
                href="https://www.instagram.com/b.ruff.writes/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-instagram-link"
                className="inline-flex items-center gap-2 text-base text-ink hover:text-terracotta transition-colors group"
              >
                <Instagram size={20} />
                <span className="link-underline">@b.ruff.writes</span>
                <span className="text-xs text-ink-soft tracking-wide ml-1 opacity-70 group-hover:opacity-100">
                  on Instagram
                </span>
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 rise-in rise-delay-2">
            <img
              src={photo}
              alt={name}
              data-testid="about-portrait"
              className="w-full h-[560px] object-cover"
            />
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-ink-soft italic">
              "{quote}"
            </p>
          </div>
        </div>
      </section>

      <section className="border-y border-rule bg-paper-2/40">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20 text-center">
          <p
            data-testid="about-pullquote"
            className="font-serif-display text-3xl lg:text-4xl italic text-ink leading-relaxed"
          >
            "{quote}"
          </p>
          <p className="mt-6 text-xs uppercase tracking-[0.22em] text-ink-soft">
            — {name}
          </p>
        </div>
      </section>
    </div>
  );
}
